import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, RadioGroup, FormControlLabel, Radio,
  Typography, Chip, Box
} from '@mui/material';

const labelSx = {
  mb: 1,
  fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
  fontWeight: 700,
  fontSize: '14px',
  color: '#475969'
};

const textareaSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    '& fieldset': {
      borderColor: '#4759694d',
      border: '1px solid #4759694d',
      borderRadius: '4px',
    },
    '&:hover fieldset': {
      borderColor: '#475969',
      borderWidth: '1px',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#475969',
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    color: 'var(--color-blue, #475969)',
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
    color: '#475969',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '4px',
    fontSize: '12px'
  }
};

const ValidationDialog = ({ open, onClose, taskId, validatorType, onSubmit, loading }) => {
  const [decision, setDecision] = useState('approved');
  const [comment, setComment] = useState('');

  const handleSubmit = () => {
    if (decision === 'changes_requested' && !comment.trim()) return;
    onSubmit({ decision, comment: comment.trim() || null });
    setDecision('approved');
    setComment('');
  };

  const handleClose = () => {
    setDecision('approved');
    setComment('');
    onClose();
  };

  const taskName = taskId ? taskId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ px: 4 }}>Validate — {taskName}</DialogTitle>
      <DialogContent sx={{ px: 4, py: 3 }}>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Validator type badge */}
          <Box>
            <Typography variant="body2" sx={{ color: '#475969', fontSize: '14px' }}>
              You are validating as:{' '}
              <Chip
                label={validatorType === 'internal' ? 'Internal validator' : 'External validator'}
                size="small"
                sx={{
                  bgcolor: validatorType === 'internal' ? '#e3f2fd' : '#e8f5e9',
                  color: validatorType === 'internal' ? '#1976d2' : '#2e7d32',
                  fontWeight: 500,
                  ml: 0.5
                }}
              />
            </Typography>
          </Box>

          {/* Decision */}
          <Box>
            <Typography variant="body1" sx={labelSx}>Decision</Typography>
            <RadioGroup value={decision} onChange={(e) => setDecision(e.target.value)}>
              <FormControlLabel
                value="approved"
                control={<Radio size="small" sx={{ color: '#475969', '&.Mui-checked': { color: '#2e7d32' } }} />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: decision === 'approved' ? '#2e7d32' : '#475969', fontSize: '14px' }}>
                      Approve
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#72849a', fontSize: '12px' }}>
                      Content is complete and meets requirements
                    </Typography>
                  </Box>
                }
                sx={{
                  mb: 1,
                  ml: 0,
                  alignItems: 'flex-start',
                  '& .MuiRadio-root': { mt: 0.5 }
                }}
              />
              <FormControlLabel
                value="changes_requested"
                control={<Radio size="small" sx={{ color: '#475969', '&.Mui-checked': { color: '#d32f2f' } }} />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: decision === 'changes_requested' ? '#d32f2f' : '#475969', fontSize: '14px' }}>
                      Request changes
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#72849a', fontSize: '12px' }}>
                      Content needs modifications before approval
                    </Typography>
                  </Box>
                }
                sx={{
                  ml: 0,
                  alignItems: 'flex-start',
                  '& .MuiRadio-root': { mt: 0.5 }
                }}
              />
            </RadioGroup>
          </Box>

          {/* Comment */}
          <Box>
            <Typography variant="body1" sx={labelSx}>
              {decision === 'changes_requested' ? 'Comment (required)' : 'Comment (optional)'}
            </Typography>
            <TextField
              multiline
              rows={3}
              fullWidth
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={decision === 'changes_requested' ? 'Describe what needs to change...' : 'Add feedback or notes...'}
              required={decision === 'changes_requested'}
              error={decision === 'changes_requested' && !comment.trim()}
              helperText={decision === 'changes_requested' && !comment.trim() ? 'Comment is required when requesting changes' : ''}
              sx={textareaSx}
            />
          </Box>

        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 4, py: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || (decision === 'changes_requested' && !comment.trim())}
          sx={{
            textTransform: 'none !important',
            backgroundColor: decision === 'approved' ? '#475969' : '#d32f2f',
            '&:hover': { backgroundColor: decision === 'approved' ? '#374955' : '#b71c1c' }
          }}
        >
          {decision === 'approved' ? 'Submit validation' : 'Request changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidationDialog;
